/** Wareneingang: Scan → Menge → Platz → Buchen (Enter führt durch, ohne Maus). Bedient wartet_bestand nach FIFO. */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { artikelListe, lagerplaetze, wareneingang } from '../api/lager';
import type { ArtikelZeile } from '../api/typen';
import { ROUTE_TEXT, uhrzeit } from '../components/format';
import { ton } from '../components/ton';
import { Feld, Karte, Seitenkopf, Tabelle, useToast } from '../components/ui';

export function Wareneingang() {
  const qc = useQueryClient();
  const toast = useToast();
  const bins = useQuery({ queryKey: ['lagerplaetze'], queryFn: lagerplaetze, staleTime: 300_000 });
  const [code, setCode] = useState('');
  const [artikel, setArtikel] = useState<ArtikelZeile | null>(null);
  const [menge, setMenge] = useState('1');
  const [bin, setBin] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [gebucht, setGebucht] = useState<{ at: string; article_id: string; sku: string; menge: number; bin_code: string | null; verfuegbar: number | null; freigegeben: number }[]>([]);
  const scanRef = useRef<HTMLInputElement>(null);
  const mengeRef = useRef<HTMLInputElement>(null);
  useEffect(() => { (artikel ? mengeRef : scanRef).current?.focus(); (artikel ? mengeRef : scanRef).current?.select?.(); }, [artikel]);

  async function suchen(e: FormEvent) {
    e.preventDefault();
    const c = code.trim();
    if (!c) return;
    setFehler(null);
    try {
      const r = await artikelListe({ q: c, pro_seite: 20 });
      const genau = r.eintraege.find((a) => a.ean === c || a.sku.toLowerCase() === c.toLowerCase()) ?? (r.eintraege.length === 1 ? r.eintraege[0] : null);
      if (!genau) { setFehler(r.eintraege.length ? `Mehrdeutig: ${r.eintraege.length} Treffer. Genauer scannen (EAN oder SKU).` : `Kein Artikel zu „${c}“.`); ton('fehler'); return; }
      if (!genau.bestand_fuehren) { setFehler(`${genau.sku} führt keinen Bestand (${ROUTE_TEXT[genau.route as keyof typeof ROUTE_TEXT] ?? 'andere Route'}).`); ton('fehler'); return; }
      setArtikel(genau); setMenge('1'); setBin(genau.standard_bin_id ?? ''); ton('ok');
    } catch (err) { setFehler((err as Error).message); }
  }

  async function buchen(e: FormEvent) {
    e.preventDefault();
    if (!artikel) return;
    const n = Number(menge);
    if (!Number.isInteger(n) || n <= 0) { setFehler('Menge muss eine ganze Zahl über 0 sein.'); return; }
    try {
      const r = await wareneingang({ article_id: artikel.id, menge: n, bin_id: bin || null });
      const g0 = r.gebucht[0];
      setGebucht((g) => [{ at: new Date().toISOString(), article_id: g0.article_id, sku: g0.sku, menge: g0.menge, bin_code: g0.bin_code, verfuegbar: g0.verfuegbar, freigegeben: r.bedient.length }, ...g]);
      toast(`+${n} ${g0.sku} auf ${g0.bin_code ?? 'WE'} gebucht${r.bedient.length ? ` · ${r.bedient.length} Sendung(en) jetzt bereit: ${r.bedient.map((b) => b.bestellnummer).join(', ')}` : ''}`);
      setArtikel(null); setCode(''); setFehler(null);
      void qc.invalidateQueries({ queryKey: ['artikel'] }); void qc.invalidateQueries({ queryKey: ['dashboard'] });
    } catch (err) { setFehler((err as Error).message); ton('fehler'); }
  }

  return (
    <section>
      <Seitenkopf titel="Wareneingang" unter="Artikel scannen, Menge eingeben, Platz prüfen, Enter bucht. Gedeckte Sendungen in „Wartet Bestand“ werden nach Zahlungseingang bereit." />
      <div className="raster-2">
        <Karte titel="Buchen">
          {!artikel ? (
            <form onSubmit={suchen} className="formular">
              <Feld label="Artikel scannen (EAN oder SKU)"><input ref={scanRef} value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" className="gross" data-testid="we-scan" /></Feld>
              <button type="submit" className="knopf">Weiter (Enter)</button>
            </form>
          ) : (
            <form onSubmit={buchen} className="formular">
              <div className="we-artikel">
                <strong className="mono">{artikel.sku}</strong> {artikel.name}{artikel.variante ? ` · ${artikel.variante}` : ''}
                <div className="hinweis">{artikel.bestand_verborgen ? 'Bestand während der Inventur verborgen (Blindzählung)' : `Bestand jetzt: physisch ${artikel.physisch} · reserviert ${artikel.reserviert} · verfügbar ${artikel.verfuegbar}`}</div>
              </div>
              <Feld label="Menge"><input ref={mengeRef} type="number" min={1} value={menge} onChange={(e) => setMenge(e.target.value)} className="gross" data-testid="we-menge" /></Feld>
              <Feld label="Platz">
                <select value={bin} onChange={(e) => setBin(e.target.value)} data-testid="we-platz">
                  <option value="">Wareneingang (WE)</option>
                  {bins.data?.eintraege.map((b) => <option key={b.id} value={b.id}>{b.code}{b.id === artikel.standard_bin_id ? ' (Standard)' : ''}</option>)}
                </select>
              </Feld>
              <div className="knopfreihe links">
                <button type="submit" className="knopf" data-testid="we-buchen">Buchen (Enter)</button>
                <button type="button" className="knopf-zweit" onClick={() => { setArtikel(null); setFehler(null); }}>Anderer Artikel</button>
              </div>
            </form>
          )}
          {fehler && <p className="fehler" role="alert">{fehler}</p>}
        </Karte>
        <Karte titel={`In dieser Sitzung gebucht (${gebucht.length})`}>
          <Tabelle zeilen={gebucht} schluessel={(g) => g.at} leer="Noch nichts gebucht"
            spalten={[
              { kopf: 'Zeit', zelle: (g) => uhrzeit(g.at) },
              { kopf: 'SKU', zelle: (g) => <Link to={`/artikel/${g.article_id}`} className="mono">{g.sku}</Link> },
              { kopf: 'Menge', zelle: (g) => `+${g.menge}`, rechts: true },
              { kopf: 'Platz', zelle: (g) => g.bin_code ?? 'WE', klasse: 'mono' },
              { kopf: 'Verfügbar', zelle: (g) => g.verfuegbar ?? 'verborgen', rechts: true },
              { kopf: 'Freigegeben', zelle: (g) => g.freigegeben || '–', rechts: true },
            ]} />
        </Karte>
      </div>
    </section>
  );
}
